import { Box, makeStyles, Typography, Dialog, DialogContent, Chip } from '@material-ui/core';
import React from 'react';
import clsx from 'clsx';
import { theme, userDarkMode } from 'theme';
import { formatDataTag } from 'features/home/utils/dataMapping';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { useHistory } from 'react-router-dom';
import { useStore } from 'features/home/store/homeSlice';
import { shallow } from 'zustand/shallow';
import { handleRouter } from 'utils/restaurantFeatures';

const useStyles = makeStyles({
  root: {},
  vertical: {
    padding: '1.2rem 1rem',
    background: !userDarkMode ? 'linear-gradient(180deg, #424750 0%, #202326 100%)' : '#414144',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    borderRadius: '24px',
    boxSizing: 'border-box',
    color: theme.palette.primary.contrastText,
    width: '13.7rem',
    minHeight: '20rem',
    flexDirection: 'column',
    marginRight: '2.3rem',
    position: 'relative',

    '& $image': {
      height: '10.6rem',
      width: '100%',
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      borderRadius: 10,
      cursor: 'pointer',
    },

    '& $cardContent': {
      flexDirection: 'column',
      padding: '1rem 0.5rem 0 0.5rem',
      flex: 1,

      '& $text': {
        fontSize: '1.2rem',
        color: theme.palette.grey[100],
      },

      '& $title': {
        fontWeight: 800,
        wordBreak: 'break-word',
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
      },

      '& $costBox': {
        '& $cost': {
          fontWeight: 600,
        },
      },
    },

    '& $addButton': {
      position: 'absolute',
      bottom: '-1.2rem',
      right: '-0.7rem',
      shadow: '0px 10px 40px rgba(0, 0, 0, 0.08)',
      cursor: 'pointer',
    },
  },
  horizontal: {
    width: '100%',
    marginTop: 30,
    '&:last-child': {
      marginBottom: 15,
    },

    '& $image': {
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      height: 120,
      width: 120,
      borderRadius: 6,
      boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.08)',
      cursor: 'pointer',
      flexShrink: 0,
      marginLeft: '1px',
    },

    '& $cardContent': {
      flex: 1,
      paddingInline: 16,
      flexDirection: 'column',

      '& $text': {
        fontSize: '1.2rem',
        color: theme.palette.grey[800],
      },

      '& $title': {
        fontWeight: 800,
        fontSize: 17,
        width: '100%',
        lineHeight: '2.4rem',

        [theme.breakpoints.down('xs')]: {
          fontSize: 16,
          wordBreak: 'break-word',
        },
      },

      '& $description': {
        paddingTop: 5,
        fontWeight: 400,
        fontSize: 14,
        color: theme.palette.text.secondary,
        flex: 1,
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        '-webkit-line-clamp': 1,
        '-webkit-box-orient': 'vertical',
        wordBreak: 'break-word',

        [theme.breakpoints.down('xs')]: {
          paddingTop: 0,
          fontSize: 13,
        },
      },

      '& $costBox': {
        display: 'flex',
        [theme.breakpoints.down('xs')]: {
          flexDirection: 'column',
        },

        '& $cost': {
          fontWeight: 400,
          fontSize: 16,
          marginRight: 'auto',
          color: theme.palette.text.secondary,
        },
        '& $tagBox': {
          flex: 1,
          justifyContent: 'center',
          [theme.breakpoints.down('xs')]: {
            justifyContent: 'flex-start',
          },
        },
      },
    },

    '& $addButton': {
      color: theme.palette.grey[800],
      cursor: 'pointer',
    },

    [theme.breakpoints.down('xs')]: {
      '& $image': {
        height: 120,
        width: 120,
      },

      '& $cardContent': {
        paddingInline: 13,
      },

      '& $addButton': {
        '& svg': {
          width: 22,
          height: 22,
        },
      },
    },
  },
  tag: {
    fontSize: 10,
    lineHeight: '10px',
    height: 24,
    margin: '2px 3px 2px 3px',
    backgroundColor: '#e0e0e0',
    color: '#000000',
    [theme.breakpoints.down('xs')]: {
      height: 20,
      '& .MuiChip-label': {
        padding: '0 6px',
      },
    },
  },
  cardContent: {},
  image: {},
  title: {},
  description: {},
  text: {},
  cost: {},
  addButton: {},
  unionIcon: {},
  costBox: {},
  tagBox: {},
  evaluationBox: {},
  dialogImage: {
    '& .MuiDialog-paperWidthSm': {
      maxWidth: 'none',
    },
  },
  lazyLoadImage: {
    objectFit: 'cover',
    borderRadius: '6px',
  },
  img: {
    height: '100%',
  },
});

export enum ECardItemType {
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
}

export interface ICardItemData {
  id: string;
  title: string;
  description?: string;
  thumbImageUrl: string;
  imageUrl: string;
  cost: number;
  currency: string;
  evaluation?: string | number;
  tags: string[];
  altName: string;
  isTakeawayDisabled?: boolean;
  active: boolean;
  shortName?: string;
  categoryId: string;
}

export interface ICardItemProps {
  data: ICardItemData;
  type: ECardItemType;
  isSoldOut: boolean;
}

const CardItem = ({ data, isSoldOut, type = ECardItemType.VERTICAL }: ICardItemProps) => {
  const classes = useStyles();
  const [visible, setVisible] = React.useState(false);
  const history = useHistory();

  const { state } = useStore(
    (state) => ({
      state: state.homeState,
    }),
    shallow,
  );

  const handleOnAdd = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    handleRouter({
      history,
      state,
      customUrl: `/${data.categoryId}/${data.id}`,
      fileName: 'CartItem',
    });
  };

  const renderTags = () => {
    let result: any[] = [];
    for (let i = 0; i < data.tags.length; i++) {
      if (formatDataTag(data.tags[i]) !== 'Recommended') {
        result.push(
          <Chip key={i.toString()} label={formatDataTag(data.tags[i])} className={classes.tag} />,
        );
      }
    }
    return result;
  };

  return (
    <>
      {data.active ? (
        <Box
          display="flex"
          className={clsx(
            classes.root,
            type === ECardItemType.VERTICAL ? classes.vertical : classes.horizontal,
          )}
        >
          <Box
            className={classes.image}
            onClick={(e: React.MouseEvent<HTMLDivElement>) => handleOnAdd(e)}
          >
            {data.thumbImageUrl && (
              <LazyLoadImage
                alt={data.altName}
                height="100%"
                src={data.thumbImageUrl}
                width="100%"
                effect="blur"
                className={classes.lazyLoadImage}
              ></LazyLoadImage>
            )}
          </Box>

          <Dialog open={visible} className={classes.dialogImage} onClose={() => setVisible(false)}>
            <DialogContent
              style={{
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                width: '70vw',
                height: '70vw',
                maxWidth: '450px',
                maxHeight: '450px',
                padding: 0,
                overflow: 'hidden',
              }}
            >
              <picture>
                <img
                  alt={data.altName}
                  src={data.imageUrl}
                  loading="lazy"
                  className={classes.img}
                />
              </picture>
            </DialogContent>
          </Dialog>

          <Box display="flex" className={classes.cardContent} onClick={handleOnAdd}>
            <Typography variant="h3" className={clsx(classes.text, classes.title)}>
              {data.title} {isSoldOut && <Chip label={'Sold Out'} className={classes.tag} />}
            </Typography>
            {type === ECardItemType.HORIZONTAL && data.altName && (
              <Typography variant="h3" className={clsx(classes.text, classes.description)}>
                {data.altName}
              </Typography>
            )}
            {type === ECardItemType.HORIZONTAL && (
              <Typography variant="body1" className={clsx(classes.text, classes.description)}>
                {data.description}
              </Typography>
            )}

            <Box className={classes.costBox}>
              <Typography
                variant="body1"
                className={clsx(classes.text, classes.cost)}
              >{`${data.currency} ${data.cost}`}</Typography>

              {type === ECardItemType.HORIZONTAL && (
                <Box display="flex" flexWrap="wrap" className={classes.tagBox}>
                  {renderTags()}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      ) : null}
    </>
  );
};

export default CardItem;
